import { ReactNode, useEffect } from "react"
import { usePage } from "@inertiajs/react"
import {
  applyAppearance,
  AppearanceSettings,
  DEFAULT_APPEARANCE,
  isValidThemeMode,
} from "@/lib/theme"
import { COLOR_OPTIONS_BY_NAME } from "@/lib/colorPalette"
import { FONT_OPTIONS_BY_NAME } from "@/lib/fontPalette"

interface SharedProps {
  appearance?: Partial<AppearanceSettings>
}

function normalize(raw: Partial<AppearanceSettings> | undefined): AppearanceSettings {
  if (!raw) return DEFAULT_APPEARANCE
  return {
    theme_mode: isValidThemeMode(raw.theme_mode) ? raw.theme_mode : "system",
    primary_color:
      typeof raw.primary_color === "string" && COLOR_OPTIONS_BY_NAME[raw.primary_color]
        ? raw.primary_color
        : DEFAULT_APPEARANCE.primary_color,
    secondary_color:
      typeof raw.secondary_color === "string" && COLOR_OPTIONS_BY_NAME[raw.secondary_color]
        ? raw.secondary_color
        : DEFAULT_APPEARANCE.secondary_color,
    heading_font:
      typeof raw.heading_font === "string" && FONT_OPTIONS_BY_NAME[raw.heading_font]
        ? raw.heading_font
        : DEFAULT_APPEARANCE.heading_font,
    body_font:
      typeof raw.body_font === "string" && FONT_OPTIONS_BY_NAME[raw.body_font]
        ? raw.body_font
        : DEFAULT_APPEARANCE.body_font,
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const page = usePage<{ props: SharedProps }>()
  const appearance = normalize((page.props as unknown as SharedProps).appearance)

  // Re-apply theme whenever the shared appearance props change (e.g. after
  // the user saves a new theme in Settings).
  useEffect(() => {
    applyAppearance(appearance)
  }, [appearance.theme_mode, appearance.primary_color, appearance.secondary_color, appearance.heading_font, appearance.body_font])

  // Keep the theme in sync with OS-level preference changes when using
  // "system" mode.
  useEffect(() => {
    if (appearance.theme_mode !== "system" || typeof window === "undefined") return
    const mql = window.matchMedia("(prefers-color-scheme: dark)")
    const listener = () => applyAppearance(appearance)
    mql.addEventListener("change", listener)
    return () => mql.removeEventListener("change", listener)
  }, [appearance.theme_mode, appearance.primary_color, appearance.secondary_color, appearance.heading_font, appearance.body_font])

  return <>{children}</>
}
